import React, { useState, useEffect } from 'react';
import { X, Loader2, Minus, Plus, Search, Check } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { createPosition, updatePosition, getMentionableUsers } from '../api';
import { Position, User, HC_EMPLOYMENT_LABELS } from '../types';
import { PRIORITY_OPTIONS, DepartmentSelect, SubDepartmentSelect, SearchableSelect, useDepartments } from './positionShared';

interface Props {
  currentUser: User;
  /** 传入则为编辑模式，不传为创建模式 */
  position?: Position | null;
  onClose: () => void;
  /** 保存成功回调；goBinding 为 true 表示用户点了「下一步：编制绑定」 */
  onSaved: (position: Position, goBinding: boolean) => void;
}

/** 优先级只展示高/中/低三档，与截图一致；储备等历史值在编辑时仍保留可选 */
const PRIORITY_CHOICES = PRIORITY_OPTIONS.filter(o => ['P0', 'P1', 'P2'].includes(o.value));

const EMPLOYMENT_CHOICES = Object.entries(HC_EMPLOYMENT_LABELS).map(([value, label]) => ({ value, label }));

export default function CreatePositionDrawer({ currentUser, position, onClose, onSaved }: Props) {
  const isEdit = !!position;
  const deptHook = useDepartments();
  const [users, setUsers] = useState<{ id: string; display_name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: position?.name || '',
    department: position?.department || '',
    sub_department: position?.sub_department || '',
    owner_id: position?.owner_id || currentUser.id,
    employment_type: position?.employment_type || 'fulltime',
    location: position?.location || '',
    priority: position?.priority || 'P1',
    expected_onboard_date: position?.expected_onboard_date || '',
    headcount_target: position?.headcount_target || 1,
    description: position?.description || '',
  });
  const set = <K extends keyof typeof form>(key: K, value: typeof form[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // 面试官：选中后保存时写入 user_positions 自动授权，并持久化为岗位级默认面试官（供「发起评审」自动带出）
  // 编辑态从 position.interviewer_ids 回填，避免保存时把已有面试官清空
  const [interviewerIds, setInterviewerIds] = useState<string[]>(() => {
    try {
      const parsed = JSON.parse(position?.interviewer_ids || '[]');
      return Array.isArray(parsed) ? parsed.filter((x: any) => typeof x === 'string' && x) : [];
    } catch { return []; }
  });
  const [ivOpen, setIvOpen] = useState(false);
  const [ivSearch, setIvSearch] = useState('');
  const toggleInterviewer = (id: string) =>
    setInterviewerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const filteredUsers = users.filter(u => u.display_name.toLowerCase().includes(ivSearch.trim().toLowerCase()));

  useEffect(() => {
    getMentionableUsers()
      .then(list => { if (Array.isArray(list)) setUsers(list); })
      .catch(() => {});
  }, []);

  const invalid = !form.name.trim() || !form.department.trim() || !form.owner_id || !form.priority;

  const save = async (goBinding: boolean) => {
    if (invalid) { toast.error('请先填写岗位名称、所属部门、招聘负责人与优先级'); return; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), department: form.department.trim(), sub_department: (form.sub_department || '').trim(), interviewer_ids: interviewerIds };
      const saved: Position = isEdit
        ? await updatePosition(position!.id, payload)
        // 创建一律先落草稿，发布动作留给详情页，避免未绑定编制就进招聘门户
        : await createPosition({ ...payload, status: 'draft' });
      toast.success(isEdit ? '岗位已保存' : '草稿已保存');
      onSaved(saved, goBinding);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || (isEdit ? '保存失败' : '创建失败'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] bg-white rounded-xl shadow-xl flex flex-col">
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-800">{isEdit ? '编辑岗位' : '创建岗位'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isEdit ? '修改岗位基本信息' : '先保存为草稿，绑定编制后再发布'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">基本信息</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="岗位名称" required className="col-span-2">
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="如：高级后端开发工程师"
                  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </Field>

              <Field label="岗位编号">
                <div className="w-full h-9 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-400 flex items-center">
                  {position?.code || '系统自动生成'}
                </div>
              </Field>

              <Field label="二级部门" required>
                <DepartmentSelect value={form.department} onChange={v => { set('department', v); set('sub_department', ''); }} deptHook={deptHook} />
              </Field>

              <Field label="三级部门">
                <SubDepartmentSelect department={form.department} value={form.sub_department} onChange={v => set('sub_department', v)} deptHook={deptHook} />
              </Field>

              <Field label="招聘负责人" required>
                <SearchableSelect
                  value={form.owner_id}
                  options={users.map(u => ({ label: u.display_name, value: u.id }))}
                  placeholder="选择负责人"
                  emptyLabel="未指定"
                  searchPlaceholder="搜索成员..."
                  onChange={v => set('owner_id', v)}
                />
              </Field>

              <Field label="岗位类型">
                <select value={form.employment_type} onChange={e => set('employment_type', e.target.value)}
                  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
                  {EMPLOYMENT_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>

              <Field label="工作地点">
                <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="如：上海"
                  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </Field>

              <Field label="期望到岗时间">
                <input type="date" value={form.expected_onboard_date} onChange={e => set('expected_onboard_date', e.target.value)}
                  className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </Field>

              <Field label="优先级" required className="col-span-2">
                <div className="flex items-center gap-2">
                  {PRIORITY_CHOICES.map(o => (
                    <button key={o.value} type="button" onClick={() => set('priority', o.value)}
                      className={clsx('px-3 h-9 rounded-lg text-sm font-medium border transition-all',
                        form.priority === o.value ? o.color : 'text-gray-500 border-gray-200 hover:bg-gray-50')}>
                      {o.short}（{o.value}）
                    </button>
                  ))}
                  {/* 存量的「长期储备」等值在编辑时保留，避免保存后被静默改档 */}
                  {form.priority && !PRIORITY_CHOICES.some(o => o.value === form.priority) && (
                    <span className="px-3 h-9 rounded-lg text-sm border border-gray-200 text-gray-500 flex items-center">
                      {PRIORITY_OPTIONS.find(o => o.value === form.priority)?.label || form.priority}
                    </span>
                  )}
                </div>
              </Field>

              <Field label="招聘人数" className="col-span-2">
                <div className="flex items-center gap-3">
                  <div className="flex items-center h-9 rounded-lg border border-gray-200 overflow-hidden">
                    <button type="button" onClick={() => set('headcount_target', Math.max(1, form.headcount_target - 1))}
                      className="w-9 h-full text-gray-500 hover:bg-gray-50 flex items-center justify-center">
                      <Minus size={14} />
                    </button>
                    <span className="w-12 text-center text-sm font-medium tabular-nums">{form.headcount_target}</span>
                    <button type="button" onClick={() => set('headcount_target', form.headcount_target + 1)}
                      className="w-9 h-full text-gray-500 hover:bg-gray-50 flex items-center justify-center">
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="text-xs text-gray-400">将通过绑定编制控制实际招聘数量</span>
                </div>
              </Field>

              <Field label="面试官" className="col-span-2">
                <div className="relative">
                  {/* 已选面试官 chips */}
                  <div className="min-h-9 w-full px-2 py-1.5 rounded-lg border border-gray-200 flex flex-wrap items-center gap-1.5">
                    {interviewerIds.length === 0 && (
                      <span className="text-sm text-gray-400 px-1">未选择（选后自动授权，并作为发起评审的默认面试官）</span>
                    )}
                    {interviewerIds.map(id => {
                      const u = users.find(x => x.id === id);
                      return (
                        <span key={id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-primary-50 text-primary-700 text-xs">
                          {u?.display_name || id}
                          <button type="button" onClick={() => toggleInterviewer(id)} className="hover:bg-primary-100 rounded p-0.5">
                            <X size={11} />
                          </button>
                        </span>
                      );
                    })}
                    <button type="button" onClick={() => setIvOpen(o => !o)}
                      className="ml-auto inline-flex items-center gap-1 px-2 h-7 rounded-md text-xs text-primary-600 hover:bg-primary-50">
                      <Plus size={12} />添加面试官
                    </button>
                  </div>

                  {/* 下拉选择面板 */}
                  {ivOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                      <div className="p-2 border-b border-gray-100 flex items-center gap-2">
                        <Search size={13} className="text-gray-400" />
                        <input autoFocus value={ivSearch} onChange={e => setIvSearch(e.target.value)}
                          placeholder="搜索成员…" className="flex-1 text-sm focus:outline-none" />
                      </div>
                      <div className="max-h-48 overflow-y-auto py-1">
                        {filteredUsers.length === 0 ? (
                          <div className="px-3 py-4 text-center text-xs text-gray-400">无匹配成员</div>
                        ) : filteredUsers.map(u => {
                          const checked = interviewerIds.includes(u.id);
                          return (
                            <button key={u.id} type="button" onClick={() => toggleInterviewer(u.id)}
                              className="w-full px-3 py-1.5 flex items-center justify-between text-sm hover:bg-gray-50">
                              <span className="text-gray-700">{u.display_name}</span>
                              {checked && <Check size={14} className="text-primary-600" />}
                            </button>
                          );
                        })}
                      </div>
                      <div className="p-2 border-t border-gray-100 text-right">
                        <button type="button" onClick={() => setIvOpen(false)}
                          className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">完成</button>
                      </div>
                    </div>
                  )}
                </div>
              </Field>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">岗位描述</h3>
            <Field label="职位简介">
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5}
                placeholder="岗位职责、团队情况、业务背景等..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            </Field>
          </section>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <button onClick={onClose} className="h-9 px-4 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            取消
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => save(false)} disabled={saving || invalid}
              className="h-9 px-4 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">
              {isEdit ? '保存' : '保存草稿'}
            </button>
            <button onClick={() => save(true)} disabled={saving || invalid}
              className="h-9 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-40 flex items-center gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />}
              下一步：编制绑定
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, className, children }: {
  label: string; required?: boolean; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="text-xs text-gray-500 mb-1 block">
        {label}{required && <span className="text-gray-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
